import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { ConnectionDetail, SalesforceSettingsPanel } from '@viq-admin/screens';
import { integrationsApi } from '../../api/integrationsApi';
import { HubSpotMappingEditView, IntegrationEditView } from './edit';

/**
 * CRM connection detail — read-only by default, Edit flips the form in place.
 * Thin wrapper around the shared `ConnectionDetail`
 * (`packages/screens/src/integrations/ConnectionDetail.tsx`, p6 Task 2).
 * Refine's `useOne`/`useDelete` are gone; `ConnectionDetail` owns its own
 * data fetching (react-query over `integrationsApi.getConnection`), the
 * read/edit toggle, the delete confirm + toast, and — for salesforce rows —
 * the Test Connection button, all parameterized by the injected `api`. For
 * hubspot rows it renders the injected `hubspotEditView` (Task 8 — the
 * HubSpot mapping form, below); zoho rows still get a "Managed by Connect"
 * note in the edit slot, since zoho has no self-serve config at all
 * (source-generic gating lives entirely in the shared component now).
 *
 * `read Integration` gates the route (App.tsx's `RequireCan`); `manage
 * Integration` gates Edit/Test/Delete inside `ConnectionDetail` via
 * `manageCap`.
 *
 * `ConnectionDetail` exposes no `onSaved`/editing-state callback — its
 * `editing` boolean is fully internal (see `edit.tsx`'s doc comment for why).
 * `version` forces a remount on a successful save via `key`: a fresh
 * `ConnectionDetail` instance starts back in its default read mode, and its
 * `useQuery(['integrationsConnection', id])` reads the already-invalidated,
 * fresh cache entry `edit.tsx` just wrote before calling `onSaved`.
 *
 * `dirty` (fix round 1) is held HERE, wired form → detail exactly as the OLD
 * `IntegrationDetail`/`IntegrationEdit` did: `edit.tsx`'s injected form
 * reports it via `onDirtyChange={setDirty}`, and it's forwarded to
 * `ConnectionDetail`'s `dirty` prop, which `DetailPage` uses to guard Cancel
 * with a discard-unsaved-changes confirm. No explicit "clear dirty on any
 * exit" handler is needed here (unlike the old code): `editView`/`readView`
 * are mutually exclusive branches of `DetailPage`'s body, so every Edit click
 * mounts a FRESH `IntegrationEditView` → fresh `SalesforceConnectionForm`,
 * whose own mount effect reports `onDirtyChange(false)` before the operator
 * can interact with it — that alone prevents a stale `dirty=true` from a
 * prior dirty-cancel-discard session from phantom-guarding the next session's
 * first Cancel (see the regression test in `IntegrationDetail.test.tsx`).
 * `onSaved` also resets it defensively, matching the old code, though the
 * `version` remount above makes that redundant in practice.
 *
 * `hubspotEditView` (Task 8) is wired the SAME way as `editView` — a fresh
 * `HubSpotMappingEditView` passed unconditionally on every render.
 * `ConnectionDetail` decides internally whether to actually mount it (only
 * for a `crmSource === 'hubspot'` row; see its own doc comment on the
 * three-way edit gate), exactly as it already did for `editView`/salesforce
 * before this prop existed — passing an unused element here costs nothing
 * since JSX only describes it, it doesn't run its body until rendered. Both
 * slots share ONE `dirty`/`version` pair: they are mutually exclusive per
 * row (a row is never both salesforce and hubspot), so only the one actually
 * mounted ever calls either callback.
 *
 * `salesforceSettingsView` (spec 2026-09-28 §4.5) is the Console-only
 * Salesforce settings panel; `ConnectionDetail` shows it under a Salesforce
 * row's read view for `manage:Integration`.
 */
export function IntegrationDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [version, setVersion] = useState(0);
  const [dirty, setDirty] = useState(false);
  const queryClient = useQueryClient();

  if (!id) return null;

  function handleSaved() {
    setDirty(false);
    setVersion((v) => v + 1);
  }

  return (
    <ConnectionDetail
      key={version}
      id={id}
      api={integrationsApi}
      manageCap="manage:Integration"
      onBack={() => navigate('/integrations')}
      onNavigate={({ href }) => navigate(href)}
      dirty={dirty}
      editView={<IntegrationEditView id={id} onDirtyChange={setDirty} onSaved={handleSaved} />}
      hubspotEditView={
        <HubSpotMappingEditView id={id} onDirtyChange={setDirty} onSaved={handleSaved} />
      }
      salesforceSettingsView={(connection) => (
        <SalesforceSettingsPanel
          connection={connection}
          save={async (profile) => {
            const saved = await integrationsApi.updateSalesforceProfile!(connection.id, profile);
            // Refresh the detail's cached row so the read view and the
            // panel's Remove button reflect what was just stored.
            await queryClient.invalidateQueries({ queryKey: ['integrationsConnection', connection.id] });
            return saved;
          }}
        />
      )}
    />
  );
}
